import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import Window from "../components/Window";
import ChooseUsername from "../components/ChooseUsername";
import Icon, { IconTile } from "../components/Icon";
import { Avatar, SpoonMeter } from "../components/ProfileBits";
import AvatarMaker from "../components/AvatarMaker";
import { MyPlant, MyShelf } from "../components/Plant";
import ChangePassword from "../components/ChangePassword";
import MyAccount from "../components/MyAccount";
import { FlagPicker, FlagRow } from "../components/Flags";
import { normalizeFlags } from "../flags";
import { useBlocks } from "../blocks";
import { unblockUser } from "../penpal";
import { AvatarConfig, DEFAULT_AVATAR, normalizeAvatar } from "../avatars";
import {
  ACCESS_OPTIONS,
  AVATAR_TONES,
  CURRENTLY_FIELDS,
  Currently,
  MAX_STICKERS,
  PATTERNS,
  PATTERN_CLASS,
  Pattern,
  REPLY_PACES,
  SPOON_LABELS,
  STICKERS,
  THEMES,
  ThemeTone,
  TileTone,
} from "../profileOptions";
import "../styles.css";

type ProfileForm = {
  name: string;
  pronouns: string;
  age: string;
  location: string;
  hobbies: string;
  topics: string;
  preferences: string;
  goals: string;
  profileRequests: string;
  // customization
  avatar: string; // older icon avatar, kept for compatibility
  avatarConfig: AvatarConfig;
  avatarTone: TileTone;
  theme: ThemeTone;
  pattern: Pattern;
  spoons: number;
  replyPace: string;
  access: string[];
  accessNotes: string;
  boundaries: string;
  currently: Currently;
  stickers: string[];
  flags: string[];
  visibility: {
    name: boolean;
    pronouns: boolean;
    age: boolean;
    location: boolean;
    showOnBoard: boolean;
  };
};

const initialForm: ProfileForm = {
  name: "",
  pronouns: "",
  age: "",
  location: "",
  hobbies: "",
  topics: "",
  preferences: "",
  goals: "",
  profileRequests: "",
  avatar: "",
  avatarConfig: DEFAULT_AVATAR,
  avatarTone: "pink",
  theme: "pink",
  pattern: "plain",
  spoons: 0,
  replyPace: "",
  access: [],
  accessNotes: "",
  boundaries: "",
  currently: {},
  stickers: [],
  flags: [],
  visibility: {
    name: false,
    pronouns: true,
    age: true,
    location: true,
    showOnBoard: true,
  },
};

export default function Profile() {
  const [user, authLoading] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<ProfileForm>(initialForm);
  const [needsUsername, setNeedsUsername] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      // If auth is still checking, keep waiting
      if (authLoading) return;

      // If no user is logged in, stop loading so they see the empty form or login prompt
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);

        setNeedsUsername(!docSnap.exists() || !docSnap.data().username);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setForm((prev) => ({
            ...prev,
            ...data,
            access: Array.isArray(data.access) ? data.access : [],
            stickers: Array.isArray(data.stickers) ? data.stickers : [],
            flags: normalizeFlags(data.flags),
            currently: { ...(data.currently || {}) },
            avatarConfig: normalizeAvatar(data.avatarConfig),
            visibility: { ...prev.visibility, ...(data.visibility || {}) },
          }));
        }
      } catch (error) {
        console.error("Profile load error:", error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user, authLoading]);

  const set = <K extends keyof ProfileForm>(key: K, value: ProfileForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleIn = (key: "access" | "stickers", value: string, max = Infinity) =>
    setForm((prev) => {
      const list = prev[key];
      if (list.includes(value)) return { ...prev, [key]: list.filter((v) => v !== value) };
      if (list.length >= max) return prev;
      return { ...prev, [key]: [...list, value] };
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    try {
      await setDoc(doc(db, "users", user.uid), form, { merge: true });
      alert("Profile saved!");
    } catch (err) {
      alert("Error saving profile. Check console.");
      console.error(err);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="window">
        <p className="empty">
          Loading your profile...
          <button
            onClick={() => setLoading(false)}
            className="mt-3 block w-full text-xs underline"
          >
            Taking too long? Click here.
          </button>
        </p>
      </div>
    );
  }

  return (
    <>
      <Window title="my_profile.htm">
        <div className="flex flex-wrap items-center gap-4">
          <Avatar config={form.avatarConfig} tone={form.avatarTone} size={64} />
          <div className="min-w-0 flex-1">
            <h1 className="window-heading">Your profile</h1>
            <p className="window-sub !mb-0">
              Make this space yours. Everything here is optional; share as
              much or as little as feels right.
            </p>
          </div>
          {user && (
            <Link to={`/profile/${user.uid}`} className="btn btn-plain btn-sm">
              <Icon name="eye" size={16} /> See how others see it
            </Link>
          )}
        </div>
      </Window>

      {user && needsUsername && (
        <ChooseUsername uid={user.uid} onDone={() => setNeedsUsername(false)} />
      )}

      {user && (
        <div className="grid items-start gap-5 md:grid-cols-[220px_minmax(0,1fr)]">
          <MyPlant uid={user.uid} />
          <Window title="my_garden.txt" tone="mint">
            <p className="mb-3 text-sm text-ink-soft">
              Water your plant once a day whenever you visit. It never wilts,
              so take all the time you need. Fully grown plants go on your
              shelf (shown on your profile), and a new seed takes their place.
            </p>
            <MyShelf uid={user.uid} />
          </Window>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <Window title="my_look.cfg" tone={form.theme}>
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_200px]">
            <div className="flex flex-col gap-4">
              <AvatarMaker
                value={form.avatarConfig}
                onChange={(avatarConfig) => set("avatarConfig", avatarConfig)}
              />

              <div className="field">
                <span className="field-label">Avatar background</span>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Avatar color">
                  {AVATAR_TONES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className="pick"
                      aria-pressed={form.avatarTone === t}
                      aria-label={t}
                      onClick={() => set("avatarTone", t)}
                    >
                      <IconTile name="heart" tone={t} size={30} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="field">
                <span className="field-label">Window color</span>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Window color">
                  {THEMES.map((t) => (
                    <button
                      key={t.tone}
                      type="button"
                      className="pick flex-row gap-1"
                      aria-pressed={form.theme === t.tone}
                      onClick={() => set("theme", t.tone)}
                    >
                      <span className="swatch" style={{ background: t.swatch }} />
                      <span className="text-xs font-bold">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="field">
                <span className="field-label">Background pattern</span>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Background pattern">
                  {PATTERNS.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      className="pick"
                      aria-pressed={form.pattern === p.value}
                      onClick={() => set("pattern", p.value)}
                    >
                      <span
                        className={`swatch !rounded-md bg-paper ${PATTERN_CLASS[p.value]}`}
                      />
                      <span className="text-xs font-bold">{p.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* live mini preview */}
            <div className="field">
              <span className="field-label">Preview</span>
              <div className={`window ${form.theme}`}>
                <div className="window-title">
                  <span className="window-title-text">my_space.htm</span>
                </div>
                <div
                  className={`window-body flex flex-col items-center gap-2 text-center ${PATTERN_CLASS[form.pattern]}`}
                >
                  <Avatar config={form.avatarConfig} tone={form.avatarTone} size={104} />
                  <p className="font-display font-bold text-tpink-deep">
                    {(form.visibility.name && form.name) || "you!"}
                  </p>
                  <FlagRow ids={form.flags} height={14} />
                  <SpoonMeter spoons={form.spoons} />
                </div>
              </div>
            </div>
          </div>
        </Window>

        <Window title="my_flags.ini" tone="blue">
          <FlagPicker value={form.flags} onChange={(flags) => set("flags", flags)} />
        </Window>

        <Window title="the_basics.txt" tone="tan">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Display name">
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="input"
                placeholder="What should pals call you?"
              />
            </Field>
            <Field label="Pronouns">
              <input
                name="pronouns"
                value={form.pronouns}
                onChange={handleChange}
                className="input"
                placeholder="e.g. she/they, any pronouns"
              />
            </Field>
            <Field label="Age">
              <input
                name="age"
                value={form.age}
                onChange={handleChange}
                className="input"
                inputMode="numeric"
              />
            </Field>
            <Field label="Location">
              <input
                name="location"
                value={form.location}
                onChange={handleChange}
                className="input"
                placeholder="City, region, or just a vibe"
              />
            </Field>
          </div>
        </Window>

        <Window title="privacy_settings.ini" tone="blue">
          <p className="mb-3 text-sm text-ink-soft">
            Choose what other members can see. Unticked details stay private.
          </p>
          <div className="grid gap-1 sm:grid-cols-2">
            {[
              { label: "Show my display name", key: "name" },
              { label: "Show my pronouns", key: "pronouns" },
              { label: "Show my age", key: "age" },
              { label: "Show my location", key: "location" },
              { label: "List me in the Pen Pal Directory", key: "showOnBoard" },
            ].map((item) => (
              <label key={item.key} className="check">
                <input
                  type="checkbox"
                  checked={
                    form.visibility[item.key as keyof typeof form.visibility]
                  }
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      visibility: {
                        ...prev.visibility,
                        [item.key]: e.target.checked,
                      },
                    }))
                  }
                />
                <span className="text-sm font-semibold">{item.label}</span>
              </label>
            ))}
          </div>
        </Window>

        <Window title="energy_and_pace.txt" tone="lavender">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="field">
              <span className="field-label">Spoons lately</span>
              <p className="text-sm text-ink-soft">
                Lets pals know how much energy you have right now. Change it
                whenever you like.
              </p>
              <div className="flex flex-wrap items-center gap-1" role="radiogroup" aria-label="Spoons lately">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={form.spoons === n}
                    aria-label={`${n} spoons: ${SPOON_LABELS[n]}`}
                    className="pick !p-1"
                    onClick={() => set("spoons", form.spoons === n ? 0 : n)}
                  >
                    <Icon
                      name="spoon"
                      size={26}
                      tone={n <= form.spoons ? "pink" : "ink"}
                      className={n <= form.spoons ? "" : "spoon-empty"}
                    />
                  </button>
                ))}
                <span className="ml-2 font-pixel text-xs">
                  {form.spoons ? SPOON_LABELS[form.spoons] : "not shown"}
                </span>
              </div>
            </div>

            <Field label="I usually write back">
              <select
                name="replyPace"
                value={form.replyPace}
                onChange={handleChange}
                className="input"
              >
                <option value="">Don't show</option>
                {REPLY_PACES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </Window>

        <Window title="what_lights_me_up.txt">
          <div className="flex flex-col gap-4">
            <Field label="About me: hobbies and interests">
              <textarea
                name="hobbies"
                value={form.hobbies}
                onChange={handleChange}
                rows={3}
                className="input"
                placeholder="Zines, birdwatching, horror movies, crochet..."
              />
            </Field>
            <Field label="Topics I'd love to talk about">
              <textarea
                name="topics"
                value={form.topics}
                onChange={handleChange}
                rows={3}
                className="input"
                placeholder="Film, video games, fishing..."
              />
            </Field>
            <div className="field">
              <span className="field-label">
                Stickers (pick up to {MAX_STICKERS}) · {form.stickers.length}/{MAX_STICKERS}
              </span>
              <div className="chip-row">
                {STICKERS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={form.stickers.includes(s)}
                    onClick={() => toggleIn("stickers", s, MAX_STICKERS)}
                    className={`chip${form.stickers.includes(s) ? " on" : ""}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Window>

        <Window title="currently.txt" tone="mint">
          <div className="grid gap-4 sm:grid-cols-2">
            {CURRENTLY_FIELDS.map((f) => (
              <Field key={f.key} label={f.label}>
                <input
                  value={form.currently[f.key] || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      currently: { ...prev.currently, [f.key]: e.target.value },
                    }))
                  }
                  className="input"
                />
              </Field>
            ))}
          </div>
        </Window>

        <Window title="how_i_communicate.txt" tone="blue">
          <div className="flex flex-col gap-4">
            <div className="field">
              <span className="field-label">Communication & access</span>
              <div className="grid gap-1 sm:grid-cols-2">
                {ACCESS_OPTIONS.map((opt) => (
                  <label key={opt} className="check">
                    <input
                      type="checkbox"
                      checked={form.access.includes(opt)}
                      onChange={() => toggleIn("access", opt)}
                    />
                    <span className="text-sm font-semibold">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
            <Field label="Anything else about how you communicate?">
              <textarea
                name="accessNotes"
                value={form.accessNotes}
                onChange={handleChange}
                rows={2}
                className="input"
                placeholder="e.g. I write best in the mornings; emoji help me read tone"
              />
            </Field>
            <Field label="Topics I'd rather not discuss">
              <textarea
                name="boundaries"
                value={form.boundaries}
                onChange={handleChange}
                rows={2}
                className="input"
                placeholder="No need to explain why. Pals will see this before writing."
              />
            </Field>
          </div>
        </Window>

        <Window title="how_to_connect.txt" tone="tan">
          <div className="flex flex-col gap-4">
            <Field label="Who I'd like to meet">
              <textarea
                name="preferences"
                value={form.preferences}
                onChange={handleChange}
                rows={3}
                className="input"
              />
            </Field>
            <Field label="Anything else pals should know?">
              <textarea
                name="profileRequests"
                value={form.profileRequests}
                onChange={handleChange}
                rows={3}
                className="input"
              />
            </Field>
          </div>
        </Window>

        <div className="text-center">
          <button type="submit" className="btn px-8 py-3 text-lg">
            <Icon name="save" size={20} /> Save my profile
          </button>
        </div>
      </form>

      <ChangePassword />

      <BlockedList />

      <MyAccount />
    </>
  );
}

// People this user has blocked, with a way to unblock.
function BlockedList() {
  const [user] = useAuthState(auth);
  const { list } = useBlocks();
  return (
    <Window title={`blocked.list (${list.length})`} tone="tan">
      {list.length === 0 ? (
        <p className="text-sm text-ink-soft">
          You haven't blocked anyone. If someone makes you uncomfortable, use
          "Block" on their profile, ad, letter, or request.
        </p>
      ) : (
        <>
          <p className="mb-3 text-sm text-ink-soft">
            Blocked people can't send you requests, letters, or kudos, and you
            won't see their ads. They aren't told.
          </p>
          <ul className="flex flex-col gap-2">
            {list.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold">@{b.username || "someone"}</span>
                <button
                  type="button"
                  className="btn btn-plain btn-sm"
                  onClick={() => user && unblockUser(user.uid, b.id)}
                >
                  Unblock
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Window>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}
